import * as React from 'react';

type ScrollRoot = 'viewport' | 'nearest-scroll-parent';

interface UseInfiniteScrollProps {
  /**
   * Whether there are more pages to load
   */
  hasNextPage: boolean | undefined;
  /**
   * Whether the next page is currently being fetched
   */
  isFetchingNextPage: boolean;
  /**
   * Function to fetch the next page
   */
  fetchNextPage: () => void;
  /**
   * Which element to observe intersections against.
   *
   * - `'viewport'` — uses the browser viewport (default IntersectionObserver behavior)
   * - `'nearest-scroll-parent'` — walks up the DOM to find the closest ancestor with
   *    `overflow-y: auto | scroll` and uses it as root. Needed when the list lives inside
   *    a scrollable container (e.g. a div with `overflow-y-auto`).
   *
   * @default 'nearest-scroll-parent'
   */
  scrollRoot?: ScrollRoot;
  /**
   * Margin around the root before triggering (CSS margin syntax)
   * @default '100px'
   */
  rootMargin?: string;
}

/**
 * Hook that provides infinite scroll via IntersectionObserver.
 *
 * Returns a callback ref to attach to a sentinel element at the bottom of the list.
 *
 * @example
 * ```tsx
 * // Auto-detect scroll container (works inside overflow-y-auto divs)
 * const sentinelRef = useInfiniteScroll({ hasNextPage, isFetchingNextPage, fetchNextPage });
 *
 * // Force viewport mode (page-level scroll)
 * const sentinelRef = useInfiniteScroll({ hasNextPage, isFetchingNextPage, fetchNextPage, scrollRoot: 'viewport' });
 *
 * return (
 *   <div>
 *     {items.map(...)}
 *     {hasNextPage && <div ref={sentinelRef} className="h-4 w-full" />}
 *   </div>
 * );
 * ```
 */
function useInfiniteScroll(props: UseInfiniteScrollProps): (node: HTMLDivElement | null) => void {
  const {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    scrollRoot = 'nearest-scroll-parent',
    rootMargin = '100px',
  } = props;

  const loadMore = React.useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Keep loadMore in a ref so the observer callback always calls the latest version
  const loadMoreRef = React.useRef(loadMore);
  loadMoreRef.current = loadMore;

  // Stable observer reference that persists across re-renders
  const observerRef = React.useRef<IntersectionObserver | null>(null);

  // Keep a reference to the sentinel node for re-observation
  const sentinelNodeRef = React.useRef<HTMLDivElement | null>(null);

  // Track previous isFetchingNextPage to detect fetch completion
  const wasFetchingRef = React.useRef(isFetchingNextPage);

  // When a fetch completes, re-observe the sentinel to trigger another load
  // if the sentinel is still visible (e.g. not enough items to push it out of view)
  React.useEffect(() => {
    const wasFetching = wasFetchingRef.current;
    wasFetchingRef.current = isFetchingNextPage;

    if (wasFetching && !isFetchingNextPage && observerRef.current && sentinelNodeRef.current) {
      observerRef.current.disconnect();
      observerRef.current.observe(sentinelNodeRef.current);
    }
  }, [isFetchingNextPage]);

  // Find the nearest scrollable ancestor of an element
  const findScrollParent = React.useCallback((element: HTMLElement): HTMLElement | null => {
    let current = element.parentElement;
    while (current) {
      const style = getComputedStyle(current);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }, []);

  // Callback ref: called when the sentinel div mounts/unmounts in the DOM
  const sentinelRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      sentinelNodeRef.current = node;

      if (node) {
        const root = scrollRoot === 'nearest-scroll-parent' ? findScrollParent(node) : null;
        observerRef.current = new IntersectionObserver(
          (entries) => {
            const [entry] = entries;
            if (entry.isIntersecting) {
              loadMoreRef.current();
            }
          },
          {
            root,
            rootMargin,
            threshold: 0,
          },
        );
        observerRef.current.observe(node);
      }
    },
    [findScrollParent, scrollRoot, rootMargin],
  );

  return sentinelRef;
}

export { useInfiniteScroll };
export type { UseInfiniteScrollProps, ScrollRoot };
